import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../../../lib/api";
import { listScans, scanSite, type ScanListItem, type SiteScan } from "../../../lib/siteScan";

/** Idle -> busy -> done/error. Idle exists because a scan is never automatic. */
export type ScanState =
  | { kind: "idle" }
  | { kind: "busy"; target: string }
  | { kind: "done"; scan: SiteScan }
  | { kind: "error"; messageKey: string };

/**
 * The stages the progress rail walks through while one request is in flight.
 *
 * The API answers with a single response, so these are honest labels for work
 * that really is happening in that order rather than a fake progress bar: the
 * page is fetched, the extra documents are probed, then the catalog runs. They
 * advance on a timer tuned to a typical scan (two to four seconds) and stop at
 * the last stage instead of looping, so a slow site never looks finished.
 */
export const SCAN_STAGES = ["fetch", "probe", "detect", "score"] as const;
export type ScanStage = (typeof SCAN_STAGES)[number];

const STAGE_MS = 900;

/**
 * The line a scan refused because the owner opted out prints. Named, because
 * the scanner tab puts a link to the do-not-scan list after exactly this line.
 */
export const SCAN_EXCLUDED_KEY = "scanner.error.excluded";

/**
 * A failed scan -> the line to print.
 *
 * Exported because the lead list opens the same report in a dialog and has to
 * fail with the same three sentences: "that is not an address", "you may not
 * refetch this", "the scan did not finish".
 */
export function scanErrorKey(err: unknown): string {
  const body = err instanceof ApiError ? (err.body as { error?: string }) : null;
  if (body?.error === "invalid_url") return "scanner.error.invalidUrl";
  if (body?.error === "forbidden") return "scanner.error.forbidden";
  // The owner asked us to stop checking the site: a reason, not a failure.
  if (body?.error === "site_excluded") return SCAN_EXCLUDED_KEY;
  return "scanner.error.failed";
}

export function useSiteScan() {
  const [state, setState] = useState<ScanState>({ kind: "idle" });
  const [stage, setStage] = useState<ScanStage>("fetch");
  const [recent, setRecent] = useState<ScanListItem[]>([]);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = () => {
    for (const t of timers.current) clearTimeout(t);
    timers.current = [];
  };

  const loadRecent = useCallback(async () => {
    try {
      // The rail is the last dozen scans, newest first - the same default the
      // table below the form uses, so the two never disagree about "recent".
      const { items } = await listScans({ pageSize: 12, sort: "checked", order: "desc" });
      setRecent(items);
    } catch {
      // The history rail is a convenience; a failure here must not block a scan.
    }
  }, []);

  useEffect(() => {
    void loadRecent();
    return clearTimers;
  }, [loadRecent]);

  const run = useCallback(
    async (url: string, refresh = false) => {
      const target = url.trim();
      if (!target) return;

      clearTimers();
      setStage("fetch");
      setState({ kind: "busy", target });
      SCAN_STAGES.forEach((s, i) => {
        if (i === 0) return;
        timers.current.push(setTimeout(() => setStage(s), STAGE_MS * i));
      });

      try {
        const scan = await scanSite(target, refresh);
        setState({ kind: "done", scan });
        void loadRecent();
      } catch (err) {
        setState({ kind: "error", messageKey: scanErrorKey(err) });
      } finally {
        clearTimers();
      }
    },
    [loadRecent],
  );

  const reset = useCallback(() => {
    clearTimers();
    setState({ kind: "idle" });
  }, []);

  return { state, stage, recent, run, reset };
}
